/* ==========================================================================
   Product Recommendations ("Frequently Bought Together")
   - Toggles selection on each card (current product is locked-on).
   - Recomputes Total and "You Save" from a JSON data blob.
   - Adds all selected products to cart in a single POST /cart/add.js call.
   - On success, opens the slide-in cart (assets/cart-drawer.js) when it is
     on the page; otherwise redirects to /cart.
   ========================================================================== */

(function () {
  'use strict';

  function init(root) {
    var dataEl = root.querySelector('script[data-pr-data]');
    if (!dataEl) return;

    var data;
    try {
      data = JSON.parse(dataEl.textContent);
    } catch (e) {
      return;
    }
    if (!data || !data.items || !data.items.length) return;

    // Map productId -> item record for quick lookup during recompute.
    var itemsById = {};
    data.items.forEach(function (item) { itemsById[item.productId] = item; });

    // Theme's Shopify.formatMoney is available (loaded by theme.liquid).
    // Falls back to plain USD if it's not present for any reason.
    function formatMoney(cents) {
      if (window.Shopify && typeof Shopify.formatMoney === 'function') {
        return Shopify.formatMoney(cents, data.moneyFormat);
      }
      return '$' + (cents / 100).toFixed(2);
    }

    var cards = Array.prototype.slice.call(root.querySelectorAll('[data-pr-card]'));
    var saveWrap = root.querySelector('[data-pr-save]');
    var saveAmount = root.querySelector('[data-pr-save-amount]');
    var totalAmount = root.querySelector('[data-pr-total-amount]');
    var addBtn = root.querySelector('[data-pr-add]');
    var ctaLabel = addBtn && addBtn.querySelector('.product-recommendations__cta-label');
    var errorEl = root.querySelector('[data-pr-error]');

    function setCardSelected(card, selected) {
      card.dataset.selected = selected ? 'true' : 'false';
      card.classList.toggle('is-selected', selected);
      card.classList.toggle('is-unselected', !selected);
      if (card.getAttribute('role') === 'button') {
        card.setAttribute('aria-pressed', selected ? 'true' : 'false');
      }
    }

    function defaultCtaLabel() {
      var n = 0;
      cards.forEach(function (c) { if (c.dataset.selected === 'true') n++; });
      return 'ADD ' + n + ' TO CART';
    }

    function recompute() {
      var totalCents = 0;
      var compareCents = 0;

      cards.forEach(function (card) {
        if (card.dataset.selected !== 'true') return;
        var item = itemsById[card.dataset.productId];
        if (!item) return;
        var price = Number(item.price) || 0;
        var compare = Number(item.compareAtPrice) || 0;
        totalCents += price;
        // Compare-at falls back to price when missing so "save" only counts real discounts.
        compareCents += compare > price ? compare : price;
      });

      if (totalAmount) totalAmount.textContent = formatMoney(totalCents);

      var savings = compareCents - totalCents;
      if (savings > 0) {
        if (saveAmount) saveAmount.textContent = formatMoney(savings);
        if (saveWrap) saveWrap.hidden = false;
      } else {
        if (saveWrap) saveWrap.hidden = true;
      }

      // Refresh the CTA label with the current selected count (skip while a
      // submit is in flight so the "Adding…" state isn't clobbered).
      if (ctaLabel && (!addBtn || !addBtn.disabled)) {
        ctaLabel.textContent = defaultCtaLabel();
      }
    }

    function toggleCard(card) {
      if (card.dataset.isCurrent === 'true') return; // locked-on
      var nowSelected = card.dataset.selected !== 'true';
      setCardSelected(card, nowSelected);
      clearError();
      recompute();
    }

    function clearError() {
      if (!errorEl) return;
      errorEl.hidden = true;
      errorEl.textContent = '';
    }

    function showError(msg) {
      if (!errorEl) return;
      errorEl.textContent = msg;
      errorEl.hidden = false;
    }

    // For the current product, read the variant id from the live product form
    // so variant selectors are honored at click time. Falls back to the
    // variant baked into JSON if the form input isn't found.
    function getCurrentProductVariantId(fallbackVariantId) {
      var formId = root.dataset.productFormId;
      if (formId) {
        var input = document.querySelector('#' + formId + ' [name="id"], form#addToCartForm-' + (formId.replace(/^productSelect-/, '')) + ' [name="id"]');
        if (input && input.value) return input.value;
      }
      // Generic fallback: the main product form on the page.
      var anyInput = document.querySelector('form[action^="/cart/add"] [name="id"]');
      if (anyInput && anyInput.value) return anyInput.value;
      return fallbackVariantId;
    }

    function buildPayload() {
      var items = [];
      cards.forEach(function (card) {
        if (card.dataset.selected !== 'true') return;
        var record = itemsById[card.dataset.productId];
        if (!record) return;
        var variantId = card.dataset.isCurrent === 'true'
          ? getCurrentProductVariantId(record.variantId)
          : record.variantId;
        items.push({ id: Number(variantId), quantity: 1 });
      });
      return { items: items };
    }

    function setLoading(loading) {
      if (!addBtn) return;
      addBtn.disabled = loading;
      if (ctaLabel) ctaLabel.textContent = loading ? 'Adding…' : defaultCtaLabel();
    }

    function handleAddToCart() {
      clearError();
      var payload = buildPayload();
      if (!payload.items.length) {
        showError('Select at least one product to add to cart.');
        return;
      }

      setLoading(true);

      // With the slide-in cart present, ask Shopify to return the drawer's
      // rendered section in the same response and hand it to the drawer.
      // The X-Cart-Drawer header stops the drawer's own request watcher from
      // handling this add twice.
      var drawer = window.sbCartDrawer;
      if (drawer) payload.sections = 'cart-drawer';

      fetch('/cart/add.js', {
        method: 'POST',
        credentials: 'same-origin',
        headers: {
          'Content-Type': 'application/json;charset=UTF-8',
          'Accept': 'application/json',
          'X-Cart-Drawer': '1'
        },
        body: JSON.stringify(payload)
      })
        .then(function (res) {
          return res.json().then(function (body) {
            return { ok: res.ok, body: body };
          });
        })
        .then(function (result) {
          if (!result.ok) {
            var msg = (result.body && (result.body.description || result.body.message)) || 'Could not add to cart. Please try again.';
            showError(msg);
            setLoading(false);
            return;
          }
          if (drawer) {
            drawer.itemAdded(result.body);
            setLoading(false);
            return;
          }
          window.location.href = '/cart';
        })
        .catch(function () {
          showError('Could not add to cart. Please try again.');
          setLoading(false);
        });
    }

    // Wire up events.
    cards.forEach(function (card) {
      card.addEventListener('click', function () { toggleCard(card); });
      // Keyboard activation for cards rendered as role="button".
      card.addEventListener('keydown', function (e) {
        if (card.getAttribute('role') !== 'button') return;
        if (e.key === 'Enter' || e.key === ' ') {
          e.preventDefault();
          toggleCard(card);
        }
      });
    });

    // Per-card variant option <select>s on recommended product cards. Each
    // product option (e.g. Quantity, Material) has its own dropdown. When any
    // option changes, we gather all current values from the card's option
    // selects, find the variant whose option1/2/3 matches, and update the
    // card's price/compare-at + variantId. Card totals + CTA count refresh
    // via recompute().
    function findVariantByOptions(record, values) {
      var ids = Object.keys(record.variants);
      for (var i = 0; i < ids.length; i++) {
        var v = record.variants[ids[i]];
        var match = true;
        for (var j = 0; j < values.length; j++) {
          if (v.options[j] !== values[j]) { match = false; break; }
        }
        if (match) return { id: ids[i], variant: v };
      }
      return null;
    }

    Array.prototype.forEach.call(root.querySelectorAll('[data-pr-card]'), function (card) {
      var optionSelects = card.querySelectorAll('[data-pr-option-select]');
      if (!optionSelects.length) return;

      // Don't let interacting with the select bubble up to toggle the card.
      Array.prototype.forEach.call(optionSelects, function (sel) {
        ['click', 'mousedown', 'keydown'].forEach(function (evt) {
          sel.addEventListener(evt, function (e) { e.stopPropagation(); });
        });
      });

      function onOptionChange(e) {
        e.stopPropagation();
        var record = itemsById[card.dataset.productId];
        if (!record || !record.variants) return;
        var values = Array.prototype.map.call(optionSelects, function (s) { return s.value; });
        var match = findVariantByOptions(record, values);
        if (!match) return;
        var v = match.variant;
        record.variantId = match.id;
        record.price = v.price;
        record.compareAtPrice = v.compareAtPrice || 0;
        card.dataset.variantId = match.id;
        var priceEl = card.querySelector('[data-pr-price]');
        var compareEl = card.querySelector('[data-pr-compare]');
        if (priceEl) priceEl.textContent = formatMoney(v.price);
        if (compareEl) {
          var hasCompare = v.compareAtPrice && v.compareAtPrice > v.price;
          if (hasCompare) {
            compareEl.textContent = formatMoney(v.compareAtPrice);
            compareEl.hidden = false;
          } else {
            compareEl.hidden = true;
          }
        }
        recompute();
      }

      Array.prototype.forEach.call(optionSelects, function (sel) {
        sel.addEventListener('change', onOptionChange);
      });
    });

    if (addBtn) addBtn.addEventListener('click', handleAddToCart);

    setupVariantSync();
    recompute();

    // ---- Variant sync ---------------------------------------------------
    // When the customer picks a different variant on the product form, the
    // "current product" card needs to reflect the new price/compare-at and
    // the totals must recompute. We listen for changes on the master variant
    // select and on the form itself, then defer reading the new value via
    // setTimeout(0) so Shopify's OptionSelectors has finished updating the
    // input before we read it.
    function setupVariantSync() {
      var currentItem = null;
      for (var i = 0; i < data.items.length; i++) {
        if (data.items[i].isCurrent) { currentItem = data.items[i]; break; }
      }
      if (!currentItem || !currentItem.variants) return;

      var formId = root.dataset.productFormId;
      if (!formId) return;

      var masterSelect = document.getElementById(formId);
      var form = masterSelect && masterSelect.closest('form');
      if (!form) {
        var altFormId = formId.replace(/^productSelect-/, 'addToCartForm-');
        form = document.getElementById(altFormId);
      }
      if (!masterSelect && !form) return;

      var currentCard = root.querySelector('[data-pr-card][data-is-current="true"]');
      var priceEl = currentCard && currentCard.querySelector('[data-pr-price]');
      var compareEl = currentCard && currentCard.querySelector('[data-pr-compare]');

      var lastVariantId = String(currentItem.variantId);

      function readVariantId() {
        if (masterSelect && masterSelect.value) return String(masterSelect.value);
        if (form) {
          var input = form.querySelector('[name="id"]');
          if (input && input.value) return String(input.value);
        }
        return null;
      }

      function applyVariant() {
        var newId = readVariantId();
        if (!newId || newId === lastVariantId) return;

        var v = currentItem.variants[newId];
        if (!v) return;

        lastVariantId = newId;
        currentItem.variantId = newId;
        currentItem.price = v.price;
        currentItem.compareAtPrice = v.compareAtPrice || 0;

        if (currentCard) currentCard.dataset.variantId = newId;
        if (priceEl) priceEl.textContent = formatMoney(v.price);
        if (compareEl) {
          var hasCompare = v.compareAtPrice && v.compareAtPrice > v.price;
          if (hasCompare) {
            compareEl.textContent = formatMoney(v.compareAtPrice);
            compareEl.hidden = false;
          } else {
            compareEl.hidden = true;
          }
        }

        recompute();
      }

      function deferred() { setTimeout(applyVariant, 0); }

      if (masterSelect) masterSelect.addEventListener('change', deferred);
      if (form && form !== masterSelect) form.addEventListener('change', deferred);
    }
  }

  function bootstrap() {
    var roots = document.querySelectorAll('[data-product-recommendations]');
    Array.prototype.forEach.call(roots, init);
  }

  if (document.readyState === 'loading') {
    document.addEventListener('DOMContentLoaded', bootstrap);
  } else {
    bootstrap();
  }
})();
